// src/pages/ProductList.jsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axios';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Tooltip,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import API_BASE_URL from '../../config';

export default function ProductList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const companyId = parseInt(localStorage.getItem('companyId'), 10);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return;

    setLoading(true);

    // جلب التصنيفات
    axios.get(`${API_BASE_URL}/Category/by-company/${companyId}`)
      .then(res => {
        setCategories(res.data.map(c => ({
          id: c.id,
          nameAr: c.nameAr
        })));
      })
      .catch(err => console.error("❌ خطأ تحميل التصنيفات:", err));

    // جلب المنتجات
    axios.get(`${API_BASE_URL}/Product/by-company/${companyId}`)
      .then(res => {
        setProducts(res.data.map(p => {
          const base = Array.isArray(p.units)
            ? p.units.find(u => u.isBaseUnit) || {}
            : {};

          return {
            id: p.id,
            code: p.code || '',
            barcode: p.barcode || '',
            arabicName: p.arabicName,
            englishName: p.englishName,
            categoryId: p.categoryId,
            categoryName: p.categoryName,
            baseUnitName: base.unitNameAr || '-',
            purchPrice: typeof base.purchprice === 'number' ? base.purchprice : null,
            salePrice: typeof base.salprice === 'number' ? base.salprice : null,
            taxRate: typeof p.taxRate === 'number' ? p.taxRate : null,
            showInPOS: !!p.showInPOS,
            isService: !!p.isService
          };
        }));
      })
      .catch(err => console.error("❌ خطأ تحميل المنتجات:", err))
      .finally(() => setLoading(false));
  }, [companyId]);

  const deleteProduct = async id => {
    if (!window.confirm('❗ هل أنت متأكد من حذف هذا المنتج؟')) return;
    try {
      await axios.delete(`${API_BASE_URL}/Product/${id}`);
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error("❌ خطأ عند الحذف:", err);
      alert('فشل حذف المنتج');
    }
  };

  const handleExportExcel = () => {
    const dataToExport = displayed.map(p => ({
      'رمز الصنف': p.code,
      'الباركود': p.barcode,
      'الاسم (عربي)': p.arabicName,
      'الاسم (إنجليزي)': p.englishName,
      'المجموعة': categories.find(c => c.id === p.categoryId)?.nameAr || '-',
      'الوحدة الأساسية': p.baseUnitName,
      'سعر الشراء': p.purchPrice,
      'سعر البيع': p.salePrice,
      'الضريبة (%)': p.taxRate
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.writeFile(wb, "products_list.xlsx");
  };

  const displayed = products
    .filter(p => {
      const s = searchTerm.trim().toLowerCase();
      return !s
        || p.arabicName.toLowerCase().includes(s)
        || (p.englishName && p.englishName.toLowerCase().includes(s))
        || (p.code && p.code.toLowerCase().includes(s))
        || (p.barcode && p.barcode.toLowerCase().includes(s));
    })
    .filter(p => {
      return !filterCategory
        || p.categoryId === parseInt(filterCategory, 10);
    });

  // عرض الكروت للجوال
  const MobileProductCard = ({ product, index }) => (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        boxShadow: 3,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              {product.arabicName}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {product.code ? `الرمز: ${product.code}` : ''} {product.barcode ? ` | الباركود: ${product.barcode}` : ''}
            </Typography>
            <Chip
              icon={<CategoryIcon />}
              label={categories.find(c => c.id === product.categoryId)?.nameAr || '-'}
              size="small"
              color="secondary"
              sx={{ mb: 1, mt: 1 }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 'bold'
            }}
          >
            #{index + 1}
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              الوحدة الأساسية
            </Typography>
            <Typography variant="body2" fontWeight="600">
              {product.baseUnitName}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              الضريبة
            </Typography>
            <Typography variant="body2" fontWeight="600">
              {product.taxRate != null ? `${product.taxRate.toFixed(2)}%` : '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              💰 سعر الشراء
            </Typography>
            <Typography variant="body2" fontWeight="600" color="success.main">
              {product.purchPrice != null ? product.purchPrice.toFixed(2) : '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              💵 سعر البيع
            </Typography>
            <Typography variant="body2" fontWeight="600" color="error.main">
              {product.salePrice != null ? product.salePrice.toFixed(2) : '-'}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/edit-product/${product.id}`)}
          sx={{ borderRadius: 2 }}
        >
          تعديل
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => deleteProduct(product.id)}
          sx={{ borderRadius: 2 }}
        >
          حذف
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <Container
      maxWidth={false}
      sx={{
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }}
      dir="rtl"
    >
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 3, sm: 4 },
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3, md: 3.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <InventoryIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'white' }} />
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="bold"
              color="white"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              📋 قائمة المنتجات
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              fontSize: { xs: '0.85rem', sm: '0.95rem' }
            }}
          >
            عدد المنتجات: {displayed.length} من {products.length}
          </Typography>
        </Box>
      </Paper>

      {/* البحث والفلتر والتصدير */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          borderRadius: 2,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="🔍 ابحث باسم المنتج، الرمز، أو الباركود..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>التصنيف</InputLabel>
              <Select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                label="التصنيف"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">كل المجموعات</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id.toString()}>
                    {cat.nameAr}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 'bold',
                height: '56px'
              }}
            >
              تصدير
            </Button>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/add-product')}
              sx={{
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 'bold',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                },
                height: '56px'
              }}
            >
              إضافة منتج
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* عرض المنتجات */}
      {isMobile ? (
        // عرض كروت للجوال
        <Box>
          {displayed.map((p, idx) => (
            <MobileProductCard key={p.id} product={p} index={idx} />
          ))}
          {displayed.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                لا توجد منتجات مطابقة
              </Typography>
            </Paper>
          )}
        </Box>
      ) : (
        // عرض جدول للشاشات الكبيرة
        <TableContainer
          component={Paper}
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                {[
                  '#',
                  'الرمز',
                  'الباركود',
                  'الاسم (عربي)',
                  'المجموعة',
                  'الوحدة الأساسية',
                  'سعر الشراء',
                  'سعر البيع',
                  'الضريبة (%)',
                  'الإجراءات'
                ].map((header, idx) => (
                  <TableCell
                    key={idx}
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: { sm: '0.9rem', md: '1rem' },
                      py: 2,
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.map((p, idx) => (
                <TableRow
                  key={p.id}
                  sx={{
                    '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background-color 0.2s',
                  }}
                >
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {idx + 1}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {p.code || '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {p.barcode || '-'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {p.arabicName}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={categories.find(c => c.id === p.categoryId)?.nameAr || '-'}
                      size="small"
                      color="secondary"
                    />
                  </TableCell>
                  <TableCell align="center">{p.baseUnitName}</TableCell>
                  <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                    {p.purchPrice != null ? p.purchPrice.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'error.main', fontWeight: 600 }}>
                    {p.salePrice != null ? p.salePrice.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {p.taxRate != null ? p.taxRate.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="تعديل">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/edit-product/${p.id}`)}
                          sx={{
                            border: '1px solid',
                            borderColor: 'primary.main',
                            '&:hover': { bgcolor: 'primary.light' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteProduct(p.id)}
                          sx={{
                            border: '1px solid',
                            borderColor: 'error.main',
                            '&:hover': { bgcolor: 'error.light' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {displayed.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary" variant="h6">
                      لا توجد منتجات مطابقة
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}